import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useHistory } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import { auth, db } from "../Config/firebase";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import RelatorioLocacaoEquipamentosPrint from "./RelatorioLocacaoEquipamentosPrint";

const hojeISO = () => new Date().toLocaleDateString("sv-SE");

function novoItem() {
  return {
    id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    nomeLocacao: "",
    quantidade: 1,
    unidade: "Mensal",
    equipamentoIds: [],
    equipamentos: [],
    acessorios: "",
    observacao: "",
  };
}

function estadoInicial() {
  return {
    codigo: "",
    clienteId: "",
    clienteNome: "",
    obraId: "",
    obraNome: "",
    dataInicio: hojeISO(),
    dataFim: "",
    status: "planejada",
    valorLocacao: "",
    observacoes: "",
    itens: [novoItem()],
  };
}

function formatarDataBR(data) {
  if (!data || !String(data).includes("-")) return data || "-";
  const [ano, mes, dia] = String(data).split("-");
  return `${dia}/${mes}/${ano}`;
}

function formatarPeriodo(dataInicio, dataFim) {
  const inicio = formatarDataBR(dataInicio);
  const possuiDataFinal = /^\d{4}-\d{2}-\d{2}$/.test(String(dataFim || ""));
  return possuiDataFinal ? `${inicio} a ${formatarDataBR(dataFim)}` : `${inicio} — Em andamento`;
}

function formatarCodigo(numero) {
  return `LOC-${String(numero).padStart(4, "0")}`;
}

function filtrarOpcoesPorNome(options, valorPesquisa) {
  const pesquisa = String(valorPesquisa || "").trim();
  if (!pesquisa) return options;

  return options.filter((option) =>
    contemTextoPesquisa(option?.nome || "", pesquisa)
  );
}

export default function CadastroLocacaoEquipamentos() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const history = useHistory();
  const [locacoes, setLocacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [locacaoEditando, setLocacaoEditando] = useState(null);
  const [locacaoRelatorio, setLocacaoRelatorio] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [buscaCliente, setBuscaCliente] = useState("");
  const [buscaObra, setBuscaObra] = useState("");
  const [form, setForm] = useState(estadoInicial());

  useEffect(() => {
    carregarDados();
  }, []);

  async function carregarDados() {
    setCarregando(true);
    try {
      const [clientesSnap, locacoesSnap] = await Promise.all([
        getDocs(consultaTenantAtual(collection(db, "clientes"))),
        getDocs(consultaTenantAtual(collection(db, "tblLocacoesEquipamentos"))),
      ]);

      setClientes(
        clientesSnap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"))
      );
      setLocacoes(
        locacoesSnap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => String(b.codigo || "").localeCompare(String(a.codigo || "")))
      );
    } finally {
      setCarregando(false);
    }
  }

  async function carregarObras(clienteId) {
    if (!clienteId) {
      setObras([]);
      return;
    }
    const snapshot = await getDocs(
      consultaTenantAtual(query(collection(db, "obras"), where("clienteId", "==", clienteId)))
    );
    setObras(
      snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((item) => item.ativo !== false)
        .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"))
    );
  }

  const clienteSelecionado = clientes.find((item) => item.id === form.clienteId) || null;
  const obraSelecionada = obras.find((item) => item.id === form.obraId) || null;

  const listaFiltrada = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    if (!termo) return locacoes;
    return locacoes.filter((item) =>
      [item.codigo, item.clienteNome, item.obraNome, item.status]
        .some((valor) => contemTextoPesquisa(String(valor || "").toLowerCase(), termo))
    );
  }, [filtro, locacoes]);

  function abrirNova() {
    setLocacaoEditando(null);
    setObras([]);
    setBuscaCliente("");
    setBuscaObra("");
    setForm(estadoInicial());
    setDialogAberto(true);
  }

  async function abrirEdicao(locacao) {
    setLocacaoEditando(locacao);
    await carregarObras(locacao.clienteId);
    setBuscaCliente(locacao.clienteNome || "");
    setBuscaObra(locacao.obraNome || "");
    setForm({
      ...estadoInicial(),
      ...locacao,
      itens: Array.isArray(locacao.itens) && locacao.itens.length
        ? locacao.itens.map((item) => ({ ...novoItem(), ...item }))
        : [novoItem()],
    });
    setDialogAberto(true);
  }

  async function salvar() {
    if (form.valorLocacao !== "" && (!Number.isFinite(Number(form.valorLocacao)) || Number(form.valorLocacao) < 0)) {
      alert("Informe um valor da locação válido, maior ou igual a zero.");
      return;
    }
    if (!form.clienteId) {
      alert("Selecione o cliente da locação.");
      return;
    }

    await executarSalvamento(async () => {
      const usuarioAtual = auth.currentUser;
      const proximoNumero = locacoes.reduce((maior, item) => {
        const numero = Number(String(item.codigo || "").replace(/\D/g, ""));
        return Number.isFinite(numero) ? Math.max(maior, numero) : maior;
      }, 99) + 1;
      const codigoLocacao = String(form.codigo || formatarCodigo(proximoNumero)).trim().toUpperCase();
      const codigoDuplicado = locacoes.some(
        (item) =>
          item.id !== locacaoEditando?.id &&
          String(item.codigo || "").trim().toUpperCase() === codigoLocacao
      );

      if (codigoDuplicado) {
        alert(`O código ${codigoLocacao} já está sendo utilizado.`);
        return;
      }

      const dados = {
        ...form,
        codigo: codigoLocacao,
        clienteNome: clienteSelecionado?.nome || form.clienteNome || "",
        obraNome: obraSelecionada?.nome || form.obraNome || "",
        itens: Array.isArray(form.itens) ? form.itens : [novoItem()],
        atualizadoEm: serverTimestamp(),
        atualizadoPor: usuarioAtual?.uid || null,
        atualizadoPorEmail: usuarioAtual?.email || "",
        atualizadoPorNome: usuarioAtual?.displayName || "",
      };

      let locacaoId;
      if (locacaoEditando?.id) {
        await updateDoc(doc(db, "tblLocacoesEquipamentos", locacaoEditando.id), dados);
        locacaoId = locacaoEditando.id;
      } else {
        const ref = await addDoc(collection(db, "tblLocacoesEquipamentos"), aplicarTenantAtual({
          ...dados,
          criadoEm: serverTimestamp(),
          criadoPor: usuarioAtual?.uid || null,
          criadoPorEmail: usuarioAtual?.email || "",
          criadoPorNome: usuarioAtual?.displayName || "",
        }));
        locacaoId = ref.id;
      }

      setDialogAberto(false);
      setLocacaoEditando(null);
      await carregarDados();
      history.push(`/app/locacao-equipamentos/${locacaoId}`);
    });
  }

  if (locacaoRelatorio) {
    return (
      <RelatorioLocacaoEquipamentosPrint
        locacao={locacaoRelatorio}
        onVoltar={() => setLocacaoRelatorio(null)}
      />
    );
  }

  return (
    <Box>
      <Navbar />
      <Box p={{ xs: 2, md: 4 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2} mb={3}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Locação de Equipamentos</Typography>
            <Typography color="text.secondary">Itens comerciais vinculados aos equipamentos cadastrados no sistema.</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNova}>
            Nova locação
          </Button>
        </Stack>

        <TextField
          fullWidth
          size="small"
          label="Pesquisar por código, cliente, obra ou status"
          value={filtro}
          onChange={(event) => setFiltro(event.target.value)}
          sx={{ mb: 2, maxWidth: 560 }}
        />

        {carregando ? (
          <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
        ) : (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Cliente / Obra</TableCell>
                  <TableCell>Período</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Itens</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listaFiltrada.map((item, indice) => (
                  <TableRow
                    key={`${item.id}-${indice}`}
                    hover
                    onClick={() => history.push(`/app/locacao-equipamentos/${item.id}`)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{item.codigo || "-"}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{item.clienteNome || "-"}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.obraNome || "Sem obra"}</Typography>
                    </TableCell>
                    <TableCell>{formatarPeriodo(item.dataInicio, item.dataFim)}</TableCell>
                    <TableCell>{item.status || "-"}</TableCell>
                    <TableCell>{item.itens?.length || 0}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Visualizar relatório">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setLocacaoRelatorio(item);
                          }}
                        >
                          <VisibilityIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar locação">
                        <IconButton size="small" onClick={(event) => { event.stopPropagation(); abrirEdicao(item); }}><EditIcon /></IconButton>
                      </Tooltip>
                      <HistoricoCadastro colecao="tblLocacoesEquipamentos" documentoId={item.id} registro={item} />
                    </TableCell>
                  </TableRow>
                ))}
                {!listaFiltrada.length && (
                  <TableRow><TableCell colSpan={6} align="center">Nenhuma locação cadastrada.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} fullWidth maxWidth="lg">
        <DialogTitle>{locacaoEditando ? `Editar ${form.codigo}` : "Nova locação de equipamentos"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {locacaoEditando && (
              <TextField
                label="Código"
                value={form.codigo || ""}
                onChange={(event) =>
                  setForm({ ...form, codigo: event.target.value.toUpperCase() })
                }
                sx={{ width: { xs: "100%", md: 220 } }}
              />
            )}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Autocomplete
                fullWidth
                options={clientes}
                filterOptions={(options) => filtrarOpcoesPorNome(options, buscaCliente)}
                getOptionLabel={(option) => option.nome || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={clienteSelecionado}
                inputValue={buscaCliente}
                onInputChange={(_, valor) => setBuscaCliente(valor)}
                onChange={(_, cliente) => {
                  carregarObras(cliente?.id || "");
                  setBuscaCliente(cliente?.nome || "");
                  setBuscaObra("");
                  setForm((anterior) => ({
                    ...anterior,
                    clienteId: cliente?.id || "",
                    clienteNome: cliente?.nome || "",
                    obraId: "",
                    obraNome: "",
                  }));
                }}
                renderInput={(params) => <TextField {...params} label="Cliente" required />}
              />
              <Autocomplete
                fullWidth
                options={obras}
                filterOptions={(options) => filtrarOpcoesPorNome(options, buscaObra)}
                getOptionLabel={(option) => option.nome || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={obraSelecionada}
                inputValue={buscaObra}
                onInputChange={(_, valor) => setBuscaObra(valor)}
                onChange={(_, obra) => {
                  setBuscaObra(obra?.nome || "");
                  setForm((anterior) => ({
                    ...anterior,
                    obraId: obra?.id || "",
                    obraNome: obra?.nome || "",
                  }));
                }}
                renderInput={(params) => <TextField {...params} label="Obra" />}
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField fullWidth type="date" label="Data inicial" InputLabelProps={{ shrink: true }} value={form.dataInicio} onChange={(e) => setForm({ ...form, dataInicio: e.target.value })} />
              <TextField fullWidth type="date" label="Data final (opcional)" helperText="Pode ficar em branco enquanto a locação estiver em andamento." InputLabelProps={{ shrink: true }} value={form.dataFim} onChange={(e) => setForm({ ...form, dataFim: e.target.value })} />
              <TextField fullWidth select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="planejada">Planejada</MenuItem>
                <MenuItem value="ativa">Ativa</MenuItem>
                <MenuItem value="devolvida">Devolvida</MenuItem>
                <MenuItem value="cancelada">Cancelada</MenuItem>
              </TextField>
            </Stack>

            <TextField label="Valor total da locação (R$)" type="number" value={form.valorLocacao ?? ""}
              onChange={(e) => setForm({ ...form, valorLocacao: e.target.value === "" ? "" : Number(e.target.value) })}
              inputProps={{ min: 0, step: "0.01" }} helperText="Uso interno e medição. Não aparece no relatório de locação." />
            <TextField multiline minRows={3} label="Observações" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAberto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : locacaoEditando ? "Salvar alterações" : "Salvar e abrir detalhes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
